import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { OrganizationResponse } from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Card, Field, Input } from '../components/ui';
import { OrgTabs } from '../components/OrgTabs';
import { fileToBase64 } from '../lib/file-upload';

const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

const BUILTIN_PRIMARY = '#2563eb';
const BUILTIN_ACCENT = '#4f46e5';
const BUILTIN_BACKGROUND = '#eff6ff';

export function OrgAuthDesignPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const readOnly = !can('organization.edit');

  const { data } = useQuery({
    queryKey: ['org'],
    queryFn: () => api<OrganizationResponse>('/org', { orgScoped: true }),
  });

  const [heading, setHeading] = useState('');
  const [welcomeText, setWelcomeText] = useState('');
  const [primaryColor, setPrimaryColor] = useState('');
  const [accentColor, setAccentColor] = useState('');
  const [backgroundColor, setBackgroundColor] = useState('');
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    const d = data.authDesign ?? {};
    setHeading(d.heading ?? '');
    setWelcomeText(d.welcomeText ?? '');
    setPrimaryColor(d.primaryColor ?? '');
    setAccentColor(d.accentColor ?? '');
    setBackgroundColor(d.backgroundColor ?? '');
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      api<OrganizationResponse>('/org', {
        method: 'PATCH',
        orgScoped: true,
        body: {
          authDesign: {
            heading: heading || undefined,
            welcomeText: welcomeText || undefined,
            primaryColor: primaryColor || undefined,
            accentColor: accentColor || undefined,
            backgroundColor: backgroundColor || undefined,
          },
        },
      }),
    onSuccess: () => {
      setSaveError(null);
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['org'] });
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (e) => setSaveError(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen.'),
  });

  const uploadImage = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > MAX_IMAGE_BYTES) throw new Error('Bild darf höchstens 2 MB groß sein.');
      const contentBase64 = await fileToBase64(file);
      return api('/org/auth-image', {
        method: 'PUT',
        orgScoped: true,
        body: { filename: file.name, mimeType: file.type, contentBase64 },
      });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['org'] }),
  });
  const resetImage = useMutation({
    mutationFn: () => api('/org/auth-image', { method: 'DELETE', orgScoped: true }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['org'] }),
  });

  const previewPrimary = primaryColor || BUILTIN_PRIMARY;
  const previewAccent = accentColor || BUILTIN_ACCENT;
  const previewBackground = backgroundColor || BUILTIN_BACKGROUND;

  return (
    <div className="space-y-4">
      <h1 className="hidden text-xl font-semibold md:block">Verein</h1>
      <OrgTabs />

      <Card title="Bild für die linke Seite">
        <p className="mb-3 text-xs text-slate-400">
          Großes Bild, das auf der Registerseite (per Einladungslink erreichbar) am Desktop die
          linke Hälfte füllt und am Mobilgerät oben erscheint. Das Vereinslogo wird automatisch
          aus den bestehenden Vereinsdaten übernommen (Verein › Design) — hier ist keine
          gesonderte Logo-Auswahl nötig.
        </p>
        <div className="flex items-center gap-4">
          {data?.authImageFileId ? (
            <img
              src={`/api/org/files/${data.authImageFileId}`}
              alt="Registerseiten-Bild"
              className="h-20 w-32 rounded border border-slate-200 object-cover"
            />
          ) : (
            <div className="flex h-20 w-32 items-center justify-center rounded border border-dashed border-slate-300 text-xs text-slate-400">
              kein Bild
            </div>
          )}
          {!readOnly && (
            <div className="min-w-0 flex-1">
              <input
                type="file"
                className="block w-full min-w-0"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                disabled={uploadImage.isPending}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) uploadImage.mutate(file);
                }}
              />
              <p className="mt-1 text-xs text-slate-400">PNG, JPEG, WebP oder SVG · max. 2 MB.</p>
              {data?.authImageFileId && (
                <button
                  type="button"
                  className="mt-1 text-xs text-slate-400 hover:text-slate-600"
                  disabled={resetImage.isPending}
                  onClick={() => resetImage.mutate()}
                >
                  Entfernen
                </button>
              )}
            </div>
          )}
        </div>
        {uploadImage.isError && (
          <Alert>{uploadImage.error instanceof Error ? uploadImage.error.message : 'Upload fehlgeschlagen.'}</Alert>
        )}
      </Card>

      <Card title="Texte & Farben">
        {saved && <Alert kind="success">Gespeichert.</Alert>}
        {saveError && <Alert>{saveError}</Alert>}
        <p className="mb-3 text-xs text-slate-400">
          Gilt für die Registerseite, die jede Person über den Einladungslink dieses Vereins
          erreicht — und, falls unter „Verein › Eigene Domain" eingerichtet, für die Login-Seite
          unter eurer eigenen Domain. Auf der allgemeinen Plattform-Adresse bleibt die
          Login-Seite weiterhin neutral, da dort noch kein Verein bekannt ist.
        </p>
        <form
          className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Field label="Überschrift">
            <Input
              value={heading}
              disabled={readOnly}
              placeholder={data?.name ?? 'Name des Vereins'}
              onChange={(e) => setHeading(e.target.value)}
            />
          </Field>
          <Field label="Begrüßungstext">
            <Input
              value={welcomeText}
              disabled={readOnly}
              placeholder="z. B. Willkommen bei uns im Verein!"
              onChange={(e) => setWelcomeText(e.target.value)}
            />
          </Field>
          <Field label="Primärfarbe (Buttons, Links)">
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="color"
                value={previewPrimary}
                disabled={readOnly}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="h-9 w-12 rounded border border-slate-300"
              />
              {primaryColor && !readOnly && (
                <button
                  type="button"
                  className="text-xs text-slate-400 hover:text-slate-600"
                  onClick={() => setPrimaryColor('')}
                >
                  auf Standard zurücksetzen
                </button>
              )}
            </div>
          </Field>
          <Field label="Akzentfarbe (Verlauf, Hervorhebungen)">
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="color"
                value={previewAccent}
                disabled={readOnly}
                onChange={(e) => setAccentColor(e.target.value)}
                className="h-9 w-12 rounded border border-slate-300"
              />
              {accentColor && !readOnly && (
                <button
                  type="button"
                  className="text-xs text-slate-400 hover:text-slate-600"
                  onClick={() => setAccentColor('')}
                >
                  auf Standard zurücksetzen
                </button>
              )}
            </div>
          </Field>
          <Field label="Hintergrundfarbe">
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="color"
                value={previewBackground}
                disabled={readOnly}
                onChange={(e) => setBackgroundColor(e.target.value)}
                className="h-9 w-12 rounded border border-slate-300"
              />
              {backgroundColor && !readOnly && (
                <button
                  type="button"
                  className="text-xs text-slate-400 hover:text-slate-600"
                  onClick={() => setBackgroundColor('')}
                >
                  auf Standard zurücksetzen
                </button>
              )}
            </div>
          </Field>
          {!readOnly && (
            <div className="sm:col-span-2">
              <Button type="submit" disabled={save.isPending}>
                Speichern
              </Button>
            </div>
          )}
        </form>
      </Card>

      <Card title="Vorschau">
        <div
          className="overflow-hidden rounded-xl border border-slate-200"
          style={{ backgroundColor: previewBackground }}
        >
          <div
            className="p-5 text-white"
            style={{ background: `linear-gradient(135deg, ${previewPrimary}, ${previewAccent})` }}
          >
            <div className="text-base font-bold">{heading || data?.name || 'Vereinsname'}</div>
            {welcomeText && <div className="mt-1 text-xs text-white/85">{welcomeText}</div>}
          </div>
          <div className="p-5">
            <div
              className="inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium text-white"
              style={{ backgroundColor: previewPrimary }}
            >
              Konto erstellen
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
